/** Map backend API errors to Chinese, user-actionable text.
 *
 * Backend errors surface as English strings with a ref code (e.g.
 * "Internal server error (ref: 3fda9d20)") or "API error: 403", which a lawyer
 * assistant cannot read or act on. This maps an `ApiError` (or any thrown value)
 * to a concise Chinese message keyed by HTTP status, with an optional context to
 * tailor the wording (e.g. comparison vs. generic document operations).
 */

import { isApiError } from "../api";

/** Operation context — tailors the wording of mapped messages. */
export type ApiErrorContext = "compare" | "document" | "draft" | "generic";

const FORBIDDEN_MESSAGES: Record<ApiErrorContext, string> = {
  compare: "暂无比对权限，请联系主办律师",
  document: "暂无该文档的操作权限，请联系主办律师",
  draft: "暂无该事项的起草权限，请联系主办律师",
  generic: "暂无操作权限，请联系主办律师",
};

const NOT_FOUND_MESSAGES: Record<ApiErrorContext, string> = {
  compare: "文档暂时无法读取，请稍后重试",
  document: "文档暂时无法读取，请稍后重试",
  draft: "起草记录不存在或已被移除",
  generic: "请求的内容不存在或已被移除",
};

const GENERIC_FAILURE = "操作失败，请稍后重试";

/**
 * Map a thrown value to a Chinese, user-facing message.
 *
 * - 401: re-login prompt (the request layer also redirects to /login).
 * - 403: no-permission, context-specific.
 * - 404: not-found / document-unreadable, context-specific.
 * - timeout / network (status 0): retry prompt.
 * - 5xx / other: generic failure.
 *
 * Non-ApiError values fall through to the generic failure message rather than
 * leaking a raw English/exception string to the user.
 */
export function mapApiError(
  err: unknown,
  context: ApiErrorContext = "generic",
): string {
  if (!isApiError(err)) return GENERIC_FAILURE;

  switch (err.status) {
    case 0:
      // Network error or client-side timeout (see ApiError construction).
      return "网络连接异常，请检查网络后重试";
    case 401:
      return "登录状态已失效，请重新登录后重试";
    case 403:
      return FORBIDDEN_MESSAGES[context];
    case 404:
      return NOT_FOUND_MESSAGES[context];
    default:
      return GENERIC_FAILURE;
  }
}
